// src/services/ProductService.js

import { 
  collection, 
  doc, 
  setDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit as firestoreLimit, 
  startAfter,
  Timestamp 
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../firebase";

const PRODUCTS_COLLECTION = "products";

// Create a new product
export const createProduct = async (productData, imageFile) => {
  try {
    // If image is provided, upload it to Firebase Storage
    let imageUrl = null;
    if (imageFile) {
      const storageRef = ref(storage, `products/${Date.now()}_${imageFile.name}`);
      const snapshot = await uploadBytes(storageRef, imageFile);
      imageUrl = await getDownloadURL(snapshot.ref);
    }
    
    // Add product to Firestore
    const docRef = await addDoc(collection(db, PRODUCTS_COLLECTION), {
      ...productData,
      image: imageUrl,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    
    // Get the newly created product
    const newProduct = await getDoc(docRef);
    return { id: newProduct.id, ...newProduct.data() };
    
  } catch (error) {
    console.error("Error creating product:", error);
    throw error;
  }
};

// Update an existing product
export const updateProduct = async (productId, productData, imageFile) => {
  try {
    const productRef = doc(db, PRODUCTS_COLLECTION, productId);
    const productDoc = await getDoc(productRef);
    
    if (!productDoc.exists()) {
      throw new Error("Product not found");
    }
    
    // Product data to update
    const updatedData = {
      ...productData,
      updatedAt: Timestamp.now()
    };
    
    // If image is provided, upload it and update the URL
    if (imageFile) {
      // Delete previous image if it exists
      const oldImageUrl = productDoc.data().image;
      if (oldImageUrl) {
        try {
          const oldImageRef = ref(storage, oldImageUrl);
          await deleteObject(oldImageRef);
        } catch (error) {
          console.warn("Error deleting old image:", error);
        }
      }
      
      // Upload new image
      const storageRef = ref(storage, `products/${Date.now()}_${imageFile.name}`);
      const snapshot = await uploadBytes(storageRef, imageFile);
      updatedData.image = await getDownloadURL(snapshot.ref);
    }
    
    // Update product in Firestore
    await updateDoc(productRef, updatedData);
    
    // Get the updated product
    const updatedProduct = await getDoc(productRef);
    return { id: updatedProduct.id, ...updatedProduct.data() };
    
  } catch (error) {
    console.error("Error updating product:", error);
    throw error;
  }
};

// Delete a product
export const deleteProduct = async (productId) => {
  try {
    const productRef = doc(db, PRODUCTS_COLLECTION, productId);
    const productDoc = await getDoc(productRef);
    
    if (!productDoc.exists()) {
      throw new Error("Product not found");
    }
    
    // Delete product image from storage if it exists
    const imageUrl = productDoc.data().image;
    if (imageUrl) {
      try {
        const imageRef = ref(storage, imageUrl);
        await deleteObject(imageRef);
      } catch (error) {
        console.warn("Error deleting image:", error);
      }
    }
    
    // Delete product document
    await deleteDoc(productRef);
    return true;
    
  } catch (error) {
    console.error("Error deleting product:", error);
    throw error;
  }
};

// Get a single product by ID
export const getProductById = async (productId) => {
  try {
    const productRef = doc(db, PRODUCTS_COLLECTION, productId);
    const productDoc = await getDoc(productRef);
    
    if (!productDoc.exists()) {
      throw new Error("Product not found");
    }
    
    return { id: productDoc.id, ...productDoc.data() };
    
  } catch (error) {
    console.error("Error getting product:", error);
    throw error;
  }
};

// Get all products
export const getAllProducts = async () => {
  try {
    const q = query(collection(db, PRODUCTS_COLLECTION), orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
  } catch (error) {
    console.error("Error getting all products:", error);
    throw error;
  }
};

// Get products by category
export const getProductsByCategory = async (categoryId) => {
  try {
    const q = query(
      collection(db, PRODUCTS_COLLECTION),
      where("categoryId", "==", categoryId),
      orderBy("createdAt", "desc")
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
  } catch (error) {
    console.error("Error getting products by category:", error);
    throw error;
  }
};

// Get featured products
export const getFeaturedProducts = async (limit = 8) => {
  try {
    const q = query(
      collection(db, PRODUCTS_COLLECTION),
      where("badge", "in", ["HOT", "NEW", "SALE"]),
      orderBy("createdAt", "desc"),
      firestoreLimit(limit) 
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
  } catch (error) {
    console.error("Error getting featured products:", error);
    throw error;
  }
};

// Get related products
export const getRelatedProducts = async (productId, categoryId, limit = 4) => {
  try {
    const q = query(
      collection(db, PRODUCTS_COLLECTION),
      where("categoryId", "==", categoryId),
      where("__name__", "!=", productId),
      firestoreLimit(limit)
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
  } catch (error) {
    console.error("Error getting related products:", error);
    throw error;
  }
};

// Pagination for products
export const getPaginatedProducts = async (lastVisible = null, pageSize = 10) => {
  try {
    let q;
    
    if (lastVisible) {
      q = query(
        collection(db, PRODUCTS_COLLECTION),
        orderBy("createdAt", "desc"),
        startAfter(lastVisible),
        firestoreLimit(pageSize)
      );
    } else {
      q = query(
        collection(db, PRODUCTS_COLLECTION),
        orderBy("createdAt", "desc"),
        firestoreLimit(pageSize)
      );
    }
    
    const querySnapshot = await getDocs(q);
    
    const products = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
    
    return { products, lastDoc };
    
  } catch (error) {
    console.error("Error getting paginated products:", error);
    throw error;
  }
};

// Search products by name
export const searchProducts = async (searchTerm) => {
  try {
    // Firebase doesn't support direct text search, so we need to get all products
    // and filter them client-side (for small datasets)
    // For larger datasets, consider using Algolia or another search service
    
    const q = query(collection(db, PRODUCTS_COLLECTION));
    const querySnapshot = await getDocs(q);
    
    const products = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter products by search term (case insensitive)
    const searchTermLower = searchTerm.toLowerCase();
    return products.filter(product => 
      product.name.toLowerCase().includes(searchTermLower) ||
      (product.description && product.description.toLowerCase().includes(searchTermLower))
    );
    
  } catch (error) {
    console.error("Error searching products:", error);
    throw error;
  }
};